import {Card} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {parseDate} from 'shared/web';

import {PriceChartItem} from './PriceChartItem';

interface PriceChartTooltipPayloadItem {
  payload?: PriceChartItem & {previousPrice?: number};
}

interface PriceChartTooltipProps {
  active?: boolean;
  payload?: PriceChartTooltipPayloadItem[];
  currency: string;
}

export function PriceChartTooltip(props: PriceChartTooltipProps) {
  const formatCurrency = useFormatCurrency();
  const formatDate = useDateTimeFormatter();

  if (!props.active || !props.payload?.length) {
    return null;
  }

  const point = props.payload[0].payload;
  if (isNil(point)) {
    return null;
  }

  const previousPrice = point.previousPrice;
  const hasDifference = isNotNil(previousPrice);
  const priceDifference = hasDifference ? point.price - previousPrice : null;
  const showDifference = hasDifference && isNotNil(priceDifference) && priceDifference !== 0;
  const dateObj = parseDate(point.date);
  const dateLabel = dateObj ? formatDate('dateTimeShort', dateObj) : '';
  const newPriceLabel = formatCurrency(point.price, props.currency) ?? '';

  return (
    <Card>
      <VStack spacing={1}>
        <Text size="xSmall" color="secondary">
          {dateLabel}
        </Text>
        <Text size="small" color="primary" alternative>
          {newPriceLabel}
        </Text>
        <Show when={showDifference ? priceDifference : null}>
          {(priceDifference) => (
            <Text size="xSmall" color={priceDifference < 0 ? 'warning' : 'danger'}>
              {priceDifference > 0 ? '+' : ''}
              {formatCurrency(Number(priceDifference), props.currency)}
            </Text>
          )}
        </Show>
      </VStack>
    </Card>
  );
}
